import { apiClient } from './client';
import type { HarmonicOrbitalModel } from '@/modules/harmonicOrbital/models/HarmonicOrbitalModel';

const BASE = '/api/v1/HarmonicOrbital';

/**
 * Harmonic Orbital de l'univers (objet UNIQUE de fin de jeu). Par défaut on ne
 * renvoie que s'il est découvert ; passer onlyDiscovered=false pour l'avoir
 * même caché (admin / debug). Renvoie null si absent ou non découvert.
 */
export async function fetchHarmonicOrbitalForUniverse(
  universeId: number,
  onlyDiscovered = true,
): Promise<HarmonicOrbitalModel | null> {
  const res = await apiClient.get<HarmonicOrbitalModel | ''>(
    `${BASE}/by-universe/${universeId}`,
    { params: { onlyDiscovered } },
  );
  // 204 No Content -> data vide.
  return res.status === 204 || !res.data ? null : (res.data as HarmonicOrbitalModel);
}

/** Marque l'objet découvert (idempotent). PlayerId dérivé du JWT côté backend. */
export async function discoverHarmonicOrbital(id: number): Promise<HarmonicOrbitalModel> {
  const { data } = await apiClient.post<HarmonicOrbitalModel>(`${BASE}/${id}/discover`);
  return data;
}

/** Palier de connaissance (Lot 2) : None / Aware / Exact. */
export type HarmonicKnowledgeTier = 'None' | 'Aware' | 'Exact';

/** Une capitale exposée (joueur en palier Exact) : sa capitale résonne, visible de tous. */
export interface ExposedCapital {
  playerUniverseId: number;
  login: string;
  coordinates: string | null;
  isSelf: boolean;
}

export interface HarmonicKnowledge {
  tier: HarmonicKnowledgeTier;
  exposedCapitals: ExposedCapital[];
}

/**
 * Connaissance du joueur sur l'emplacement du H'O + capitales exposées de son
 * univers (joueurs ayant atteint le palier Exact). Voir §38.5/§38.8.
 */
export async function fetchHarmonicKnowledge(
  playerUniverseId: number,
): Promise<HarmonicKnowledge> {
  const { data } = await apiClient.get<HarmonicKnowledge>(`${BASE}/knowledge`, {
    params: { playerUniverseId },
  });
  return data;
}

/** Active l'objet = victoire (fin de jeu). One-shot, validé propriétaire backend. */
export async function activateHarmonicOrbital(
  id: number,
  playerUniverseId: number,
): Promise<HarmonicOrbitalModel> {
  const { data } = await apiClient.post<HarmonicOrbitalModel>(`${BASE}/${id}/activate`, {
    playerUniverseId,
  });
  return data;
}
